import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKeyhole, MailCheck, ShieldCheck } from "lucide-react";
import { AUTH_DENIAL_MESSAGES, type AuthDenialReason } from "@ir/types";
import { Alert, Button, Logo, LoadingState, ThemeToggle } from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";

/**
 * Sign-in.
 *
 * The button is a plain link to `/api/auth/login` — the whole OAuth exchange
 * happens on dave-service, so no Google SDK, client secret or token ever
 * touches this bundle.
 */
export function SignInPage() {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const invite = searchParams.get("invite");
  const denied = searchParams.get("denied") as AuthDenialReason | null;
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    searchParams.get("returnTo") ??
    "/";

  if (status === "loading") {
    return <LoadingState message="Checking your access…" className="min-h-dvh" />;
  }

  if (status === "authenticated") {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <div className="relative grid min-h-dvh bg-canvas lg:grid-cols-2">
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      {/* Sign-in panel */}
      <div className="flex items-center justify-center px-5 py-12 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <Logo height={30} plate={false} className="mb-8 dark:bg-white dark:px-2 dark:py-1" />

          <h1 className="font-display text-2xl font-semibold tracking-tight text-content">
            Marketplace admin
          </h1>
          <p className="mt-2 text-sm text-content-muted">
            Sign in with your Insurance Revolution Google account.
          </p>

          {denied && (
            <Alert tone="danger" className="mt-6" title="We could not sign you in">
              {AUTH_DENIAL_MESSAGES[denied] ?? AUTH_DENIAL_MESSAGES.unknown}
            </Alert>
          )}

          {invite && !denied && (
            <Alert tone="success" className="mt-6" title="Invitation found">
              Sign in with the Google account this invite was sent to and your
              access will be activated.
            </Alert>
          )}

          <Button
            size="lg"
            fullWidth
            className="mt-7"
            icon={LockKeyhole}
            onClick={() => signIn({ returnTo, invite })}
          >
            Continue with Google
          </Button>

          <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-content-subtle">
            <ShieldCheck size={13} aria-hidden="true" className="mt-0.5 shrink-0 text-gold-500" />
            Access is granted per person by an administrator. Signing in with a
            Google account is not enough on its own.
          </p>
        </motion.div>
      </div>

      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-ink-950 lg:block">
        <span aria-hidden="true" className="absolute inset-0 bg-grid opacity-40" />
        <span
          aria-hidden="true"
          className="absolute -left-24 top-1/4 size-96 rounded-full bg-gold-500/15 blur-[100px]"
        />
        <span
          aria-hidden="true"
          className="absolute -bottom-24 right-0 size-80 rounded-full bg-crimson-600/15 blur-[100px]"
        />

        <div className="relative flex h-full flex-col justify-end p-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span aria-hidden="true" className="mb-6 block h-0.5 w-24 bg-gold-sheen" />
            <p className="max-w-md font-display text-3xl font-semibold leading-tight tracking-tight text-white">
              One place for every{" "}
              <span className="text-gold-gradient">quote, lead and policy</span> in
              the marketplace.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-ink-300">
              {[
                "Search and inspect every quote and lead",
                "Pull existing quotes back from OpenGI",
                "Complete step forms on behalf of customers",
              ].map((line) => (
                <li key={line} className="flex items-center gap-2.5">
                  <MailCheck size={14} aria-hidden="true" className="shrink-0 text-gold-500" />
                  {line}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </aside>
    </div>
  );
}
